import { apiRequest } from '../utils/requests'
import { buildUrlWithParams } from '../utils/helpers'
import type {
  Ticket,
  TicketGroup,
  TicketCategory,
  CreateTicketRequest,
  PublicCreateTicketRequest,
  UpdateTicketRequest,
  CreateTicketReplyRequest,
  TicketReply,
} from '@/types/ticket'

// ========== ADMIN TICKETS API ==========
export const adminTicketsAPI = {
  list: async (params?: Record<string, any>) => {
    const url = buildUrlWithParams('/admin/tickets', params || {})
    return apiRequest<{
      tickets: Ticket[]
      total: number
      page: number
      limit: number
    }>(url)
  },

  get: async (id: number | string) => {
    return apiRequest<{
      ticket: Ticket
      replies: TicketReply[]
      assignments: any[]
    }>(`/admin/tickets/${id}`)
  },

  getByUuid: async (uuid: string) => {
    return apiRequest<Ticket>(`/admin/tickets/uuid/${uuid}`)
  },

  create: async (data: CreateTicketRequest) => {
    return apiRequest<Ticket>('/admin/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  update: async (id: number | string, data: UpdateTicketRequest) => {
    return apiRequest<Ticket>(`/admin/tickets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  delete: async (id: number | string) => {
    return apiRequest<void>(`/admin/tickets/${id}`, {
      method: 'DELETE',
    })
  },

  assign: async (
    id: number | string,
    data: {
      assignedToUserId?: number
      assignedToTeamId?: number
      reason?: string
    }
  ) => {
    return apiRequest<Ticket>(`/admin/tickets/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  getAssignments: async (id: number | string) => {
    return apiRequest<any[]>(`/admin/tickets/${id}/assignments`)
  },

  resolve: async (id: number | string) => {
    return apiRequest<Ticket>(`/admin/tickets/${id}/resolve`, {
      method: 'POST',
    })
  },

  close: async (id: number | string) => {
    return apiRequest<Ticket>(`/admin/tickets/${id}/close`, {
      method: 'POST',
    })
  },

  getReplies: async (id: number | string) => {
    return apiRequest<TicketReply[]>(`/admin/tickets/${id}/replies`)
  },

  reply: async (id: number | string, data: CreateTicketReplyRequest) => {
    return apiRequest<TicketReply>(`/admin/tickets/${id}/replies`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// ========== AGENT TICKETS API ==========
export const agentTicketsAPI = {
  list: async (params?: Record<string, any>) => {
    const url = buildUrlWithParams('/agent/tickets', params || {})
    return apiRequest<{
      tickets: Ticket[]
      total: number
      page: number
      limit: number
    }>(url)
  },

  get: async (id: number | string) => {
    return apiRequest<{
      ticket: Ticket
      replies: TicketReply[]
      assignments: any[]
    }>(`/agent/tickets/${id}`)
  },

  create: async (data: CreateTicketRequest) => {
    return apiRequest<{ ticket: Ticket }>('/agent/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  update: async (id: number | string, data: UpdateTicketRequest) => {
    return apiRequest<Ticket>(`/agent/tickets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  reply: async (
    id: number | string,
    data: Partial<CreateTicketReplyRequest>
  ) => {
    return apiRequest<TicketReply>(`/agent/tickets/${id}/replies`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// ========== PUBLIC/GUEST TICKETS API ==========
export const publicTicketsAPI = {
  create: async (data: PublicCreateTicketRequest) => {
    return apiRequest<{ ticket: Ticket; token: string; tokenUrl: string }>(
      '/public/tickets',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  listByUserId: async (
    userId: number,
    params?: { page?: number; limit?: number }
  ) => {
    const queryParams = params
      ? new URLSearchParams({
          ...(params.page && { page: params.page.toString() }),
          ...(params.limit && { limit: params.limit.toString() }),
        }).toString()
      : ''
    const url = `/public/tickets/user/${userId}${queryParams ? `?${queryParams}` : ''}`
    return apiRequest<{
      tickets: Ticket[]
      pagination: {
        total: number
        page: number
        limit: number
        totalPages: number
      }
    }>(url)
  },

  getTokenForTicket: async (ticketId: number) => {
    return apiRequest<{ token: string }>(
      `/public/tickets/get-token/${ticketId}`
    )
  },

  getByToken: async (token: string) => {
    return apiRequest<{
      ticket: Ticket
      replies: TicketReply[]
      notification: {
        type: string
        message: string
        sentAt: Date | null
        readAt: Date | null
      }
    }>(`/public/tickets/${token}`)
  },

  reply: async (
    token: string,
    data: { content: string; attachments?: any[] }
  ) => {
    return apiRequest<TicketReply>(`/public/tickets/${token}/replies`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// ========== TICKET GROUPS & CATEGORIES API ==========
export const ticketGroupsAPI = {
  list: async () => {
    return apiRequest<TicketGroup[]>('/admin/ticket-groups')
  },

  listWithCategories: async () => {
    return apiRequest<Array<TicketGroup & { categories: TicketCategory[] }>>(
      '/admin/ticket-groups/with-categories'
    )
  },

  get: async (id: number | string) => {
    return apiRequest<TicketGroup>(`/admin/ticket-groups/${id}`)
  },

  create: async (data: Omit<TicketGroup, 'id' | 'createdAt' | 'updatedAt'>) => {
    return apiRequest<TicketGroup>('/admin/ticket-groups', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  update: async (id: number | string, data: Partial<TicketGroup>) => {
    return apiRequest<TicketGroup>(`/admin/ticket-groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  delete: async (id: number | string) => {
    return apiRequest<void>(`/admin/ticket-groups/${id}`, {
      method: 'DELETE',
    })
  },

  // Categories
  listCategories: async () => {
    return apiRequest<TicketCategory[]>('/admin/ticket-groups/categories')
  },

  getCategory: async (id: number | string) => {
    return apiRequest<TicketCategory>(`/admin/ticket-groups/categories/${id}`)
  },

  createCategory: async (
    data: Omit<TicketCategory, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    return apiRequest<TicketCategory>('/admin/ticket-groups/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  updateCategory: async (
    id: number | string,
    data: Partial<TicketCategory>
  ) => {
    return apiRequest<TicketCategory>(`/admin/ticket-groups/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  deleteCategory: async (id: number | string) => {
    return apiRequest<void>(`/admin/ticket-groups/categories/${id}`, {
      method: 'DELETE',
    })
  },
}

// Export all APIs
export const ticketsAPI = {
  admin: adminTicketsAPI,
  agent: agentTicketsAPI,
  public: publicTicketsAPI,
  groups: ticketGroupsAPI,
}
